// Helpers
import { LOCALE_FIN } from './i18n';

/**
 * Sort descending.
 *
 * @param {Object} a - First object.
 * @param {Object} b - Second object.
 * @param {string} orderBy - Order by key.
 * @param {string} [aliasOrderBy] - Order by alias key (when orderBy value would be falsy, use alias key value instead).
 * @param {(string|Function)} [secondaryOrderBy] - Secondary order by key or comparison function (when primary order by values are equal).
 * @returns {number} - Sort order.
 */
function sortDesc(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  orderBy: string,
  aliasOrderBy?: string,
  secondaryOrderBy?: string | ((a: any, b: any) => number)
) {
  let aValue = a[orderBy];
  let bValue = b[orderBy];

  // Date fields that needs conversion.
  if (
    [
      'saleStarts',
      'saleEnds',
      'last_arrived',
      'last_putaway',
      'last_putaway_dcu2_date',
      'last_putaway_dcu1_date',
      'last_arrived_dcu1_date',
      'last_arrived_abakus_date',
      'newUntil',
      'activeFromTime',
      'enableSellingTime',
      'updated',
      'updatedAt',
      'createdAt',
    ].includes(orderBy)
  ) {
    try {
      aValue = a[orderBy] ? new Date(a[orderBy] as string).getTime() || 0 : 0;
    } catch {
      aValue = 0;
    }
    try {
      bValue = b[orderBy] ? new Date(b[orderBy] as string).getTime() || 0 : 0;
    } catch {
      bValue = 0;
    }
  }

  if (aliasOrderBy && typeof aliasOrderBy === 'string') {
    if (!aValue && a[aliasOrderBy]) {
      aValue = a[aliasOrderBy];
    }

    if (!bValue && b[aliasOrderBy]) {
      bValue = b[aliasOrderBy];
    }
  }

  if (!aValue && aValue !== 0) {
    return -1;
  } else if (!bValue && bValue !== 0) {
    return 1;
  }

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }

  if (secondaryOrderBy && typeof secondaryOrderBy === 'string') {
    const secondaryAValue = a[secondaryOrderBy] as string;
    const secondaryBValue = b[secondaryOrderBy] as string;

    return String(secondaryBValue).localeCompare(String(secondaryAValue), 'fi-FI', {
      numeric: true,
    });
  } else if (secondaryOrderBy && typeof secondaryOrderBy === 'function') {
    return secondaryOrderBy(a, b);
  }

  return 0;
}

function stableSort<T extends any[]>(
  array: T,
  cmp: (a: T[number], b: T[number]) => number
): T | [] {
  if (!array) {
    return [];
  }
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]) as T;
}

/**
 * Get sorting function for table sorting.
 *
 * @param {string} order - Order direction.
 * @param {string} orderBy - Order by key.
 * @param {string} [orderByAlias] - Order by alias key (when orderBy value would be falsy, use alias key value instead).
 * @param {(string|function)} [secondaryOrderBy] - Secondary order by key or comparison function (when primary order by values are equal).
 */
function getSorting(
  order: 'asc' | 'desc',
  orderBy: string,
  orderByAlias?: string,
  secondaryOrderBy?: string | ((a: any, b: any) => number)
) {
  return order === 'desc'
    ? (a: any, b: any) => sortDesc(a, b, orderBy, orderByAlias, secondaryOrderBy)
    : (a: any, b: any) => -sortDesc(a, b, orderBy, orderByAlias, secondaryOrderBy);
}

function sortByTranslation(a: string, b: string) {
  const nameA = LOCALE_FIN[a as keyof typeof LOCALE_FIN] || '';
  const nameB = LOCALE_FIN[b as keyof typeof LOCALE_FIN] || '';
  return String(nameA).toUpperCase().localeCompare(String(nameB).toUpperCase(), 'fi-FI', {
    numeric: true,
  });
}

function sortByMappingKey(
  a: string,
  b: string,
  mapping: Record<string, Record<string, string>>,
  key: string
) {
  const nameA = mapping[a]?.[key] || '';
  const nameB = mapping[b]?.[key] || '';

  return String(nameA).toUpperCase().localeCompare(String(nameB).toUpperCase(), 'fi-FI', {
    numeric: true,
  });
}

export { stableSort, getSorting, sortByTranslation, sortByMappingKey };
